import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  InputMaybe,
  InsertChildImpactRatingsMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface InsertChildImpactRatingsTRPCInput {
  Ratings: { ImpactId: string; Rating: number }[];
  TestDate: string;
  RatedItemId: string;
  AssessmentId?: InputMaybe<string>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
  CompletedBy?: InputMaybe<string>;
  Likelihood?: InputMaybe<number>;
}

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Ids: string[];
}): InsertChildImpactRatingsMutation => {
  return {
    insertChildImpactRating: {
      __typename: 'insertChildImpactRatingOutput',
      Ids: trpcData.Ids,
    },
  };
};

export const useInsertChildImpactRatingsTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.impactRating.insertBatch.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impactRating.impactRatings.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impactRating.impactRatingById.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.impactRating.impactRatingsByImpactId.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.impactRating.impactRatingsByRatedItemId.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.impactRating.impactRatingsWithAppetites.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impactRating.impactRatingCount.queryKey(),
          }),
          // Assessment views may show rating data
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.assessment.register.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.assessment.resultsRegister.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertChildImpactRatings: async (
      variables: InsertChildImpactRatingsTRPCInput
    ): Promise<InsertChildImpactRatingsMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
